<template>
  <div class="account-list">
    <instagram-next-auth ref="instagramNextAuth"></instagram-next-auth>
    <div
      class="account"
      v-for="(account, index) in accounts"
      :key="`${account.snsType || 'unknown'}-${account.id || index}`"
      layout
      layout-align="space-between-center"
    >
      <div>
        <div layout layout-align="start-center ">
          <icon class="mr-x1" :name="account.name" :picture-url="account.pictureUrl" :icon-type="account.snsType">
          </icon>
          <div>
            <div class="mr-x2">{{ account.name }}</div>
            <div class="text-token">
              <div class="text-token-modified">
                アクセストークン更新日時:
                <span v-if="account.tokenModified">{{ formatDatetime(account.tokenModified) }}</span>
                <span v-else>-</span>
              </div>
              <div class="text-issuer">発行者: {{ account.tokenIssuer || '-' }}</div>
            </div>
          </div>
          <div class="badge badge-twitter ml-x2 mt-x1" v-if="account.isEnterprise && isBeforeNttAaaSwitchover">
            API認証済
          </div>
        </div>
        <div>
          <div class="message message-error p-x1" v-if="account.isTokenExpired === 1 || account.isWebhookExpired">
            アクセストークンの有効期限が切れています。更新してください。
          </div>
        </div>
      </div>
      <div>
        <el-button
          :type="account.isTokenExpired === 1 || account.isWebhookExpired ? 'primary' : 'default'"
          @click="updateAccountToken(account)"
          ><i class="mdi mdi-reload"></i>アクセストークン更新</el-button
        >
        <el-button
          v-if="!guestGroups.some(v => v.id === currentGroupId)"
          type="default"
          @click="deleteAccount(account.id, account.snsType)"
          ><i class="mdi mdi-delete-forever"></i>削除</el-button
        >
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.account {
  padding: 12px 0;
  border-top: 1px solid #ebeef5;

  .message {
    line-height: 14px;
  }

  .mdi-check-decagram {
    color: #04aeee;
  }

  .badge.badge-twitter {
    position: initial;
    display: inline-block;
    margin-bottom: $spacing-unit;
  }

  .text-token {
    color: #888;
  }

  .text-token-modified {
    display: inline-block;
    font-size: $font-small-size;
  }

  .text-issuer {
    display: inline-block;
    margin-left: $spacing-unit;
    font-size: $font-small-size;
  }
}
</style>
<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import Icon from '@/client/components/_atoms/basic_icon'
import api from '@/client/core/api'
import { TikTokAuthService } from '@/client/core/tiktok_auth'
import TwitterAuthService from '@/client/core/twitter_auth'
import InstagramNextAuth from '@/client/features/setting_group/instagram_next_auth'
import { formatDatetime } from '@/client/filters/filters'
import { TrackingService } from '@/client/services'
import { useSettingGroupStore } from '@/client/store/useSettingGroupStore'
import { useSystemStore } from '@/client/store/useSystemStore'
import { isBeforeNttAaaSwitchover } from '@/common/utils'
import { TCampaignAccount } from '@/typings/api/campaign_accounts.type'
type TAccount = {
  id?: string
  token?: string
  secret?: string
  enterpriseToken?: string
  enterpriseSecret?: string
  name?: string
  screenName?: string
  pictureUrl?: string
  isVerified?: boolean
  verifiedType?: string
}

export default defineComponent({
  props: {
    accounts: Array
  },
  components: {
    Icon,
    InstagramNextAuth
  },
  computed: {
    ...mapState(useSystemStore, {
      currentGroupId: state => state.currentGroup.id,
      guestGroups: state => state.guestGroups,
      allowInstantwin: state => Boolean(state.currentGroup.allowInstantwin)
    }),

    isBeforeNttAaaSwitchover
  },
  methods: {
    formatDatetime,

    ...mapActions(useSettingGroupStore, ['fetchAccounts']),

    /**
     * アカウントトークンの更新
     */
    async updateAccountToken(account: TCampaignAccount) {
      TrackingService.sendEvent('click:グループ設定|キャンペーン実施アカウント|アクセストークン更新')

      if (account.snsType === 'twitter') {
        await this.updateTwitterAccountToken(account)
      } else if (account.snsType === 'tiktok') {
        await this.updateTikTokAccountToken(account)
      } else if (account.snsType === 'instagram') {
        this.$refs.instagramNextAuth.open({ type: 'update', accountId: account.id })
      }

      // アカウント一覧を取得
      const { shouldLeaveGroupSetting } = await this.fetchAccounts(this.currentGroupId)

      if (shouldLeaveGroupSetting) {
        this.$router.push('/campaigns')
      }
    },

    /**
     * Twitterアカウントトークンの更新
     */
    async updateTwitterAccountToken(apiAccount: TCampaignAccount) {
      if (!apiAccount.accountId) return

      let account: TAccount = {}

      account = await TwitterAuthService.execAuth()

      if (!account || !Object.keys(account).length) {
        this.$notify({
          title: '認証に失敗しました。',
          message: '恐れ入りますが、時間を置いて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 認証したアカウントが違う場合
      if (account.id !== apiAccount.accountId) {
        const errorText = '認証したアカウントが違うため、アクセストークンの更新に失敗しました。'

        this.$notify({
          title: errorText,
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // Enterprise認証すると認証時のログインアカウント関係なく認証できてしまうため、ここより上でアカウントが正しいかのチェックをする
      if (apiAccount.isEnterprise && apiAccount.accountId) {
        const enterpriseAuth = await TwitterAuthService.execEnterpriseAuth(apiAccount.accountId)

        if (enterpriseAuth) {
          account = {
            ...account,
            id: enterpriseAuth.id,
            enterpriseToken: enterpriseAuth.enterpriseToken,
            enterpriseSecret: enterpriseAuth.enterpriseSecret
          }
        }
      }

      const result = await api.put('/twitter_campaign_accounts/' + apiAccount.id, {
        token: account.token,
        secret: account.secret,
        enterpriseToken: account.enterpriseToken,
        enterpriseSecret: account.enterpriseSecret,
        name: account.name,
        screenName: account.screenName,
        pictureUrl: account.pictureUrl
      })

      if (!result.data) {
        const errorText = 'アクセストークン更新処理に失敗しました。'

        this.$notify({
          title: errorText,
          message: '恐れ入りますが、時間を置いて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: `${account.name}のアクセストークンを更新しました。`,
        customClass: 'success',
        duration: 5000
      })
    },

    /**
     * TikTokアカウントトークンの更新
     */
    async updateTikTokAccountToken(apiAccount: TCampaignAccount) {
      if (!apiAccount.id) return

      const callback = await TikTokAuthService.openAuthWindow()

      if (callback.type !== 'SUCCESS') {
        this.$notify({
          title: '認証に失敗しました。',
          message: '恐れ入りますが、時間を置いて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      const ttAccount = await TikTokAuthService.getAccountInfo(callback, this.currentGroupId)

      if (!ttAccount && !ttAccount.data) {
        return this.$notify({
          title: '認証に失敗しました。',
          message: '恐れ入りますが、時間を置いて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
      }

      if (ttAccount.data.ttAccountId !== apiAccount.ttAccountId) {
        const errorText = '認証したアカウントが違うため、アクセストークンの更新に失敗しました。'

        this.$notify({
          title: errorText,
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      const body = {
        groupId: this.currentGroupId,
        accessToken: ttAccount.data.accessToken,
        refreshToken: ttAccount.data.refreshToken
      }

      const result = await api.put('/tiktok_campaign_accounts/' + apiAccount.id, body)

      if (!result.data) {
        const errorText = 'アクセストークン更新処理に失敗しました。'

        this.$notify({
          title: errorText,
          message: '恐れ入りますが、時間を置いて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: `${ttAccount.data.name}のアクセストークンを更新しました。`,
        customClass: 'success',
        duration: 5000
      })
    },

    /**
     * Twitterアカウントの削除
     * @param {string} accountId
     */
    async deleteAccount(accountId: string, snsType: string) {
      TrackingService.sendEvent('click:グループ設定|キャンペーン実施アカウント|削除')

      const confirm = window.confirm('アカウントを登録解除してよろしいですか? \n この操作は取り消しできません。')
      if (!confirm) {
        return
      }

      const apiEndpoints = {
        twitter: '/twitter_campaign_accounts/',
        instagram: '/instagram_campaign_accounts/',
        tiktok: '/tiktok_campaign_accounts/'
      }

      const result = await api.delete(apiEndpoints[snsType] + accountId)

      // 失敗した場合はエラーを表示
      if (result.error && (result.error.type === 'NOT_EXISTS' || result.error.type === 'NO_DATA_EXISTS')) {
        this.$notify({
          title: '対象のアカウントはすでに解除されています。',
          customClass: 'danger',
          duration: 5000
        })

        // アカウント一覧を取得
        const { shouldLeaveGroupSetting } = await this.fetchAccounts(this.currentGroupId)

        if (shouldLeaveGroupSetting) {
          this.$router.push('/campaigns')
        }

        return
      } else if (result.error && result.error.type === 'CAMPAIGN_SCHEDULED') {
        this.$notify({
          title: 'キャンペーン開催予定のアカウントです。',
          message: '対象のキャンペーンを削除後、再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      } else if (!result.data) {
        this.$notify({
          title: 'アカウントの登録解除に失敗しました。',
          message: '恐れ入りますが、時間をおいて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: 'アカウントの登録を解除しました。',
        customClass: 'success',
        duration: 5000
      })

      // アカウント一覧を取得
      const { shouldLeaveGroupSetting } = await this.fetchAccounts(this.currentGroupId)

      if (shouldLeaveGroupSetting) {
        this.$router.push('/campaigns')
      }
    }
  }
})
</script>
